import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import { authApi } from '@/services/api'
import { useAuthStore } from '@/store/auth'
import styles from './OTPVerify.module.css'

export default function EmailOTPVerify() {
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resendTimer, setResendTimer] = useState(60)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()
  const setTokens = useAuthStore((state) => state.setTokens)
  const setUser = useAuthStore((state) => state.setUser)

  const [email] = useState(() => sessionStorage.getItem('email') || '')

  useEffect(() => {
    if (!email) {
      navigate('/login')
      return
    }

    inputRefs.current[0]?.focus({ preventScroll: true })

    const timer = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [email, navigate])

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)
    setError('')

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus({ preventScroll: true })
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus({ preventScroll: true })
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6)
    setOtp(newOtp)

    const lastFilledIndex = pastedData.length - 1
    inputRefs.current[Math.min(lastFilledIndex + 1, 5)]?.focus({ preventScroll: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const otpString = otp.join('')

    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await authApi.verifyEmailOTP(email, otpString)
      const { access_token, refresh_token, user, is_new_user } = response.data

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (is_new_user || !(user as any).onboarding_completed) {
        navigate('/onboarding/child', { replace: true })
      } else {
        navigate('/', { replace: true })
      }

      setTokens(access_token, refresh_token)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setUser(user as any)
      sessionStorage.removeItem('email')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { code?: string; message?: string } } } }
      const errorCode = error?.response?.data?.error?.code
      const errorMessage = error?.response?.data?.error?.message

      if (errorCode === 'OTP_EXPIRED') {
        setError('OTP expired. Please request a new one.')
      } else if (errorCode === 'OTP_MAX_ATTEMPTS') {
        setError('Too many attempts. Please request a new OTP.')
      } else if (errorMessage) {
        setError(errorMessage)
      } else {
        setError('Invalid OTP. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendTimer > 0) return

    try {
      await authApi.requestEmailOTP(email)
      setResendTimer(60)
      setOtp(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus({ preventScroll: true })
    } catch {
      setError('Failed to resend OTP. Please try again.')
    }
  }

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={() => navigate('/login')}>
        ← Back
      </button>

      <div className={styles.header}>
        <h1 className={styles.title}>Verify your email</h1>
        <p className={styles.subtitle}>
          Enter the 6-digit code sent to{' '}
          <strong>{email}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.otpInputs} onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`${styles.otpInput} ${error ? styles.error : ''}`}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {error && <p className={styles.errorText}>{error}</p>}

        <Button type="submit" fullWidth size="lg" loading={loading}>
          Verify
        </Button>

        <p className={styles.resend}>
          Didn't receive the code?{' '}
          {resendTimer > 0 ? (
            <span>Resend in {resendTimer}s</span>
          ) : (
            <button type="button" onClick={handleResend} className={styles.resendButton}>
              Resend OTP
            </button>
          )}
        </p>
      </form>
    </div>
  )
}
